import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { Plus, Loader2, CheckCircle2, XCircle, Clock, Archive } from 'lucide-react';
import { format } from 'date-fns';
import { useLocalization } from '@/hooks/useLocalization';

type ApprovalStatus = 'pending' | 'approved' | 'rejected' | 'retired';

interface ModelEntry {
  id: string;
  model_name: string;
  provider: string | null;
  version: string | null;
  use_case: string | null;
  approval_status: ApprovalStatus;
  approved_at: string | null;
  created_at: string;
}

const STATUS_CONFIG: Record<ApprovalStatus, { label: string; icon: React.ReactNode; className: string }> = {
  pending:  { label: 'Pending',  icon: <Clock className="h-3 w-3" />,       className: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' },
  approved: { label: 'Approved', icon: <CheckCircle2 className="h-3 w-3" />, className: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' },
  rejected: { label: 'Rejected', icon: <XCircle className="h-3 w-3" />,      className: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
  retired:  { label: 'Retired',  icon: <Archive className="h-3 w-3" />,      className: 'bg-muted text-muted-foreground' },
};

const empty = { model_name: '', provider: '', version: '', use_case: '' };

export const ModelRegistry = () => {
  const { tx, t } = useLocalization();
  const { tenantId } = useAuth();
  const { toast } = useToast();
  const [models, setModels] = useState<ModelEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const query = supabase
      .from('ai_model_registry')
      .select('*')
      .order('created_at', { ascending: false });
    if (tenantId) query.eq('tenant_id', tenantId);
    const { data, error } = await query;
    if (!error) setModels((data ?? []) as ModelEntry[]);
    setLoading(false);
  }, [tenantId]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    if (!form.model_name.trim()) { toast({ title: t('Model name required'), variant: 'destructive' }); return; }
    setSaving(true);
    const { error } = await supabase.from('ai_model_registry').insert({
      model_name: form.model_name.trim(),
      provider: form.provider || null,
      version: form.version || null,
      use_case: form.use_case || null,
      tenant_id: tenantId,
    });
    setSaving(false);
    if (error) { toast({ title: t('Save failed'), description: error.message, variant: 'destructive' }); return; }
    toast({ title: t('Model registered') });
    setDialogOpen(false);
    setForm(empty);
    load();
  };

  const updateStatus = async (id: string, status: ApprovalStatus) => {
    const { error } = await supabase
      .from('ai_model_registry')
      .update({ approval_status: status, approved_at: status === 'approved' ? new Date().toISOString() : null })
      .eq('id', id);
    if (error) { toast({ title: t('Update failed'), description: error.message, variant: 'destructive' }); return; }
    toast({ title: t('Model {0}', { 0: status }) });
    load();
  };

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-3">
          <div>
            <CardTitle className="text-base">{t('AI Model Registry')}</CardTitle>
            <CardDescription>{t('Register and govern AI models in use across the platform')}</CardDescription>
          </div>
          <Button size="sm" className="gap-2" onClick={() => setDialogOpen(true)}>
            <Plus className="h-4 w-4" />  {t('Register Model')}
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
          ) : models.length === 0 ? (
            <div className="flex flex-col items-center py-12 text-muted-foreground text-sm">
              <p>{t('No models registered yet.')}</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="px-6 py-3 text-start text-xs font-medium text-muted-foreground uppercase">{t('Model')}</th>
                  <th className="px-6 py-3 text-start text-xs font-medium text-muted-foreground uppercase">{t('Provider')}</th>
                  <th className="px-6 py-3 text-start text-xs font-medium text-muted-foreground uppercase">{t('Use Case')}</th>
                  <th className="px-6 py-3 text-start text-xs font-medium text-muted-foreground uppercase">{t('Status')}</th>
                  <th className="px-6 py-3 text-start text-xs font-medium text-muted-foreground uppercase">{t('Registered')}</th>
                  <th className="px-6 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {models.map(m => {
                  const cfg = STATUS_CONFIG[m.approval_status];
                  return (
                    <tr key={m.id} className="hover:bg-muted/30 transition-colors">
                      <td className="px-6 py-3">
                        <p className="font-medium">{m.model_name}</p>
                        {m.version && <p className="text-xs text-muted-foreground">v{m.version}</p>}
                      </td>
                      <td className="px-6 py-3 text-muted-foreground">{m.provider ?? '—'}</td>
                      <td className="px-6 py-3 text-muted-foreground">{m.use_case ?? '—'}</td>
                      <td className="px-6 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.className}`}>
                          {cfg.icon}{tx(cfg.label)}
                        </span>
                      </td>
                      <td className="px-6 py-3 text-muted-foreground">{format(new Date(m.created_at), 'MMM d, yyyy')}</td>
                      <td className="px-6 py-3">
                        <div className="flex items-center gap-1">
                          {m.approval_status === 'pending' && (
                            <>
                              <Button size="sm" variant="ghost" className="h-7 text-xs text-emerald-600" onClick={() => updateStatus(m.id, 'approved')}>{t('Approve')}</Button>
                              <Button size="sm" variant="ghost" className="h-7 text-xs text-destructive" onClick={() => updateStatus(m.id, 'rejected')}>{t('Reject')}</Button>
                            </>
                          )}
                          {m.approval_status === 'approved' && (
                            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => updateStatus(m.id, 'retired')}>{t('Retire')}</Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{t('Register AI Model')}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2"><Label>{t('Model Name *')}</Label><Input value={form.model_name} onChange={e => setForm(f => ({ ...f, model_name: e.target.value }))} placeholder={t('e.g. llama-3.3-70b-versatile')} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>{t('Provider')}</Label><Input value={form.provider} onChange={e => setForm(f => ({ ...f, provider: e.target.value }))} placeholder={t('Groq, OpenAI…')} /></div>
              <div className="space-y-2"><Label>{t('Version')}</Label><Input value={form.version} onChange={e => setForm(f => ({ ...f, version: e.target.value }))} placeholder="1.0" /></div>
            </div>
            <div className="space-y-2"><Label>{t('Use Case')}</Label><Input value={form.use_case} onChange={e => setForm(f => ({ ...f, use_case: e.target.value }))} placeholder={t('Report generation, risk analysis…')} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>{t('Cancel')}</Button>
            <Button onClick={handleSave} disabled={saving}>{saving ? <Loader2 className="h-4 w-4 animate-spin me-2" /> : null}{t('Register')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ModelRegistry;
